/* eslint-disable @typescript-eslint/ban-ts-comment */
import { fabric } from "fabric";
import { SelectedOption, UserAsset } from "./types";
import { actions, Thunk, State } from "./module";
import selectors from "~/CustomizableToolPage/selectors";
import { getCanvasDimensions } from "./fabric/utils";

const setCartLoading: Thunk = (state, dispatch) => (loading) =>
  dispatch({ type: actions.cartLoadingChanged, payload: loading });
const changeProduct: Thunk = (state, dispatch) => (itemNumberId: string) => {
  const { items } = state;
  if (items[itemNumberId]) {
    dispatch?.({ type: actions.productChanged, payload: items[itemNumberId] });
    window.history.pushState(
      items[itemNumberId].itemNumber,
      "",
      `/customized-products/${items[itemNumberId].itemNumber}`
    );
  }
};
const changeRegion: Thunk = (state, dispatch) => (regionId: number) =>
  dispatch?.({ type: actions.regionChanged, payload: regionId });
const changeNavigationKey: Thunk = (state, dispatch) => (navigationKey: {
  key: number;
  type: string;
}) =>
  dispatch?.({ type: actions.navigationKeyChanged, payload: navigationKey });

const selectOption: Thunk = (state, dispatch) => (payload: SelectedOption) => {
  const { navigationViewModelData } = state;
  const { customizableOptions } = navigationViewModelData;
  const currentCustomizableOption = customizableOptions[payload.optionId];
  const { regions } = currentCustomizableOption;
  const currentOptionRegion = regions[payload.regionId];
  const { selectedOptions } = currentOptionRegion;

  const newState: State = {
    ...state,
    navigationViewModelData: {
      ...navigationViewModelData,
      customizableOptions: {
        ...customizableOptions,
        [payload.optionId]: {
          ...currentCustomizableOption,
          regions: {
            ...regions,
            [payload.regionId]: {
              ...currentOptionRegion,
              selectedOptions: {
                ...selectedOptions,
                [payload.attributeId]: payload.attributeValueId,
              },
            },
          },
        },
      },
    },
  };

  const userActions = selectors.getUserActionsShape(newState, dispatch)();
  if (userActions) newState.userActions = userActions;

  dispatch?.({ type: actions.optionSelected, payload: newState });
};

const addCustomizations: Thunk = (state, dispatch) => (
  payload: SelectedOption
) => {
  const { customerCustomizationData } = state;

  const customizationData = [...customerCustomizationData];

  // find existing
  const index = customizationData.findIndex(
    (cd) =>
      (cd.clientIdentifier &&
        cd.clientIdentifier === payload.clientIdentifier) ||
      // eslint-disable-next-line eqeqeq
      (cd.attributeId == payload.attributeId && cd.regionId == payload.regionId)
  );

  if (index > -1) {
    // replace
    customizationData.splice(index, 1, {
      ...payload,
    });
  } else {
    customizationData.push({
      ...payload,
    });
  }

  const newState = {
    ...state,
    customerCustomizationData: customizationData,
  };

  const userActions =
    selectors.getUserActionsShape(newState, dispatch)() || null;

  dispatch({
    type: actions.customizationsUpdated,
    payload: { customizationData, userActions },
  });
};

const removeCustomizations: Thunk = (state, dispatch) => (
  payload: SelectedOption
) => {
  const { customerCustomizationData } = state;

  const newState = {
    ...state,
    customerCustomizationData: customerCustomizationData.filter(
      (cd) => cd.clientIdentifier !== payload.clientIdentifier
    ),
  };

  const userActions =
    selectors.getUserActionsShape(newState, dispatch)() || null;

  dispatch({
    type: actions.customizationsUpdated,
    payload: {
      customizationData: newState.customerCustomizationData,
      userActions,
    },
  });
};

const clearCustomizations: Thunk = (state, dispatch) => (regionId: number) => {
  const { customerCustomizationData } = state;

  const newState = {
    ...state,
    customerCustomizationData: customerCustomizationData.filter(
      (cd) => cd.regionId !== regionId
    ),
  };

  const userActions = selectors.getUserActions(newState, dispatch)();

  dispatch({
    type: actions.customizationsUpdated,
    payload: {
      customizationData: newState.customerCustomizationData,
      userActions,
    },
  });
};

const generateAllRegionDesigns: Thunk = (state) => async () => {
  const {
    fabric: { regions },
    regionData,
  } = state;

  const promises = Object.entries(regions).map(([regionId, json]) => {
    const canvas = new fabric.Canvas(document.createElement("canvas"));
    return new Promise<{ regionId: string; canvasImage: string }>(
      (resolve, reject) => {
        const region = regionData[regionId];

        if (!region) reject();

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const dimensions = getCanvasDimensions(region!);
        canvas.setHeight(dimensions.height);
        canvas.setWidth(dimensions.width);

        canvas.loadFromJSON(json, () => {
          resolve({
            regionId,
            canvasImage: canvas.toDataURL(), // Convert to image
          });
        });
      }
    );
  });

  const results = await Promise.all(promises);
  return results;
};

const addUserAssets: Thunk = (state, dispatch) => (
  images: UserAsset[] = []
) => {
  const payload: Lookup<UserAsset> = {};

  images.forEach((image) => {
    payload[image.path] = image;
  });

  dispatch({ type: actions.userAssetsAdded, payload });
};

export default {
  setCartLoading,
  changeProduct,
  changeRegion,
  changeNavigationKey,
  selectOption,
  addCustomizations,
  removeCustomizations,
  clearCustomizations,
  generateAllRegionDesigns,
  addUserAssets,
};
